import { ImageWithLoader, ModalTitle } from '@overlayqa/shared/ui';
import { timeAgo } from '@/lib/utils';
/**
 * VerificationCard (dashboard) — Tailwind variant of the extension's BEM card.
 *
 * Renders one of 7 states for resolved-issue verification (spec §7.1 in
 * docs/superpowers/plans/2026-05-14-fix-verification.md):
 *
 *   A) No verification yet     -> [Verify fix]
 *   B) In progress             -> progress bar + optional [Cancel]
 *   C) Passed                  -> [Mark as verified] / [Reopen]
 *   D) Passed with caveat      -> [Mark verified]    / [Reopen]
 *   E) Failed                  -> [Reopen as regressed] / [Retry]
 *   F) Inconclusive            -> [Mark as verified] / [Reopen] / [Retry]
 *   G) Error                   -> [Try again] / [Skip verification]
 *
 * Behavior mirrors `packages/extension/src/content/verify/VerificationCard.tsx`
 * 1:1. Prop shape is identical (`VerificationCardProps`) so future shared-
 * component refactoring is mechanical.
 *
 * Styling uses dashboard tokens (`card-sm`, `section-label`, `primary-*`,
 * `var(--color-*)`) per CLAUDE.md UI guidelines. No emoji — status icons are
 * inline SVG.
 *
 * Note: The dashboard cannot run the capture itself (capture needs browser
 * access to the live page). The consumer (Task 5.9) wires `onVerify` to a
 * single-issue handoff via `#oq-verify-issue=<id>` page fragment.
 */

import { useEffect, useRef, useState } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import type { ReactNode } from 'react';
import type { IssueVerification, VerificationStatus } from '@overlayqa/shared';
import { useSimulatedVerifyProgress } from './simulatedVerifyProgress';
import { ModalPortal } from '@/components/shared/ModalPortal';

export interface VerificationCardProps {
  readOnly?: boolean;
  /** The issue detail disclosure supplies the surrounding card. */
  embedded?: boolean;
  issueId: string;
  verification: IssueVerification | null;
  beforeScreenshotUrl?: string;
  inProgress?: { stage: 'capturing' | 'running_axe' | 'sending_to_ai'; percent: number } | null;
  history?: IssueVerification[];
  onVerify?: () => void;
  onMarkVerified: () => void;
  onReopen: () => void;
  onRetry: () => void;
  onSkip: () => void;
  onCancel?: () => void;
  /** Which status action (mark verified / reopen) is mid-flight. Disables
      every action button and swaps the active one's label so a double-click
      can't fire the handler twice (see useIssueVerification). */
  statusActionPending?: 'verify' | 'reopen' | null;
}

type InProgressStage = NonNullable<VerificationCardProps['inProgress']>['stage'];

const STAGE_LABEL: Record<InProgressStage, string> = {
  capturing: 'Capturing',
  running_axe: 'Running axe-core',
  sending_to_ai: 'Sending to AI',
};

const BADGE_CLASS: Record<VerificationStatus, string> = {
  pending: 'bg-primary-50 text-primary-700',
  passed: 'bg-green-50 text-green-700',
  passed_with_caveat: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
  inconclusive: 'bg-gray-100 text-gray-700',
  error: 'bg-red-50 text-red-700',
};

const BADGE_LABEL: Record<VerificationStatus, string> = {
  pending: 'Verifying',
  passed: 'Fix verified',
  passed_with_caveat: 'Passed (with caveat)',
  failed: 'Fix failed',
  inconclusive: 'Inconclusive',
  error: "Couldn't verify",
};

// Reusable button classes built from existing Tailwind utilities. The dashboard
// has `btn-primary` defined in index.css, but it uses `bg-primary-500` (one
// shade lighter) and a larger radius; verification actions intentionally use
// 600 to feel slightly heavier next to status badges. The action matrix only
// needs primary + ghost (no secondary).
const BTN_PRIMARY =
  'inline-flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST =
  'inline-flex items-center justify-center text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export function VerificationCard(props: VerificationCardProps): JSX.Element {
  const { verification, beforeScreenshotUrl, inProgress, history } = props;
  const className = props.embedded ? 'mt-4' : 'card-sm mb-4';
  const [showFull, setShowFull] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [lightboxInitial, setLightboxInitial] = useState<'before' | 'after' | null>(null);

  // ───── State B — in progress ────────────────────────────────────────
  if (inProgress) {
    return (
      <InProgressView
        className={className}
        stage={inProgress.stage}
        percent={inProgress.percent}
        onCancel={props.readOnly ? undefined : props.onCancel}
      />
    );
  }

  // ───── State A — no verification yet ────────────────────────────────
  if (!verification) {
    return <NoVerificationView className={className} readOnly={props.readOnly} onVerify={props.onVerify} />;
  }

  // ───── States C/D/E/F/G — render from verification.status ───────────
  const status = verification.status;

  return (
    <section className={className}>
      <header className="mb-3 flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${BADGE_CLASS[status]}`}
        >
          <StatusIcon status={status} />
          <span>{BADGE_LABEL[status]}</span>
        </span>
        <span className="text-xs text-gray-500">{timeAgo(verification.createdAt, { showSeconds: true, round: true, dateAfterDays: 1 })}</span>
      </header>

      <div className="pt-4 space-y-3">
        {/* Thumbs (skipped for error state) */}
        {status !== 'error' && verification.afterScreenshotUrl && beforeScreenshotUrl && (
          <div className="grid grid-cols-2 gap-3">
            <Thumb
              url={beforeScreenshotUrl}
              label="BEFORE"
              onClick={() => setLightboxInitial('before')}
            />
            <Thumb
              url={verification.afterScreenshotUrl}
              label="AFTER"
              onClick={() => setLightboxInitial('after')}
            />
          </div>
        )}

        {(verification.contextScreenshots?.before || verification.contextScreenshots?.after) && (
          <div className="flex flex-wrap gap-3 text-sm">
            {verification.contextScreenshots.before && <a className="text-primary-600 hover:underline" href={verification.contextScreenshots.before} target="_blank" rel="noopener noreferrer"><span className="text-cap-trim block">Original page context</span></a>}
            {verification.contextScreenshots.after && <a className="text-primary-600 hover:underline" href={verification.contextScreenshots.after} target="_blank" rel="noopener noreferrer"><span className="text-cap-trim block">Current page context</span></a>}
          </div>
        )}

        {/* Explanation (non-error) */}
        {status !== 'error' && verification.explanation && (
          <p className="text-sm text-gray-700 leading-relaxed">
            {capFirstTwoSentences(verification.explanation)}
            {verification.explanationFull && (
              <>
                {' '}
                <button
                  type="button"
                  className="text-primary-600 hover:underline text-sm"
                  onClick={() => setShowFull((s) => !s)}
                >
                  <span className="text-cap-trim block">{showFull ? 'less' : 'more detail'}</span>
                </button>
                {showFull && (
                  <pre className="mt-2 p-3 bg-gray-50 border border-gray-100 rounded-md text-xs text-gray-700 whitespace-pre-wrap font-mono">
                    {verification.explanationFull}
                  </pre>
                )}
              </>
            )}
          </p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-700 leading-relaxed">
            {verification.errorMessage ?? 'Verification could not run.'}
          </p>
        )}

        {/* Meta line — axe / confidence / viewport mismatch */}
        {hasMeta(verification) && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
            {verification.axeResult && <span>axe: {verification.axeResult}</span>}
            {verification.confidence != null && (
              <span>AI confidence: {verification.confidence.toFixed(2)}</span>
            )}
            {verification.viewportMismatch && <span>viewport mismatch</span>}
          </div>
        )}

        {/* Actions */}
        {!props.readOnly && <div className="flex flex-wrap gap-2 pt-1">{renderActions(status, props)}</div>}

        {/* History (collapsible) */}
        {history && history.length > 0 && (
          <details
            className="border-t border-gray-100 pt-3 mt-1"
            open={showHistory}
            onToggle={(e) => setShowHistory((e.target as HTMLDetailsElement).open)}
          >
            <summary className="section-label cursor-pointer select-none">
              History ({history.length} prior verification
              {history.length === 1 ? '' : 's'})
            </summary>
            <div className="mt-2 space-y-1">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-2 text-xs text-gray-500"
                >
                  <span>{BADGE_LABEL[h.status]}</span>
                  <span aria-hidden="true">&middot;</span>
                  <span>{new Date(h.createdAt).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Full-resolution lightbox (opened by clicking a thumbnail) */}
      {lightboxInitial !== null &&
        beforeScreenshotUrl &&
        verification.afterScreenshotUrl && (
          <Lightbox
            beforeUrl={beforeScreenshotUrl}
            afterUrl={verification.afterScreenshotUrl}
            initial={lightboxInitial}
            onClose={() => setLightboxInitial(null)}
          />
        )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────────────

interface NoVerificationViewProps {
  readOnly?: boolean;
  className: string;
  onVerify?: () => void;
}

function NoVerificationView({ onVerify, className, readOnly }: NoVerificationViewProps): JSX.Element {
  return (
    <section className={className}>
      <header className="mb-3">
        <strong className="text-sm font-semibold text-gray-900">No verification yet</strong>
      </header>
      <div className="pt-4 space-y-3">
        <p className="text-sm text-gray-700 leading-relaxed">
          The fix hasn&apos;t been verified.{!readOnly && ' Re-capture this element and let AI compare it to the original.'}
        </p>
        {!readOnly && <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className={BTN_PRIMARY} onClick={onVerify}>
            <span className="text-cap-trim block">Verify fix</span>
          </button>
        </div>}
      </div>
    </section>
  );
}

interface InProgressViewProps {
  className: string;
  stage: InProgressStage;
  percent: number;
  onCancel?: () => void;
}

/**
 * State B view. Mounted only while a verify run is in flight, so the
 * timer-driven progress hook starts fresh per run. The caller's coarse
 * milestone (`percent`) is a floor; the bar keeps easing toward 95% on a
 * timer during the long opaque await (server capture + AI) instead of
 * freezing at the last milestone. See simulatedVerifyProgress.ts.
 */
function InProgressView({ stage, percent, onCancel, className }: InProgressViewProps): JSX.Element {
  const displayPercent = useSimulatedVerifyProgress(percent);
  return (
    <section className={className}>
      <header className="mb-3">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${BADGE_CLASS.pending}`}
        >
          <Spinner />
          <span>Verifying...</span>
        </span>
      </header>
      <div className="pt-4 space-y-3">
        <p className="text-sm text-gray-700">{STAGE_LABEL[stage]}</p>
        <div
          className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={displayPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-primary-500 transition-[width] duration-200 ease-out"
            style={{ width: `${displayPercent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500">Usually takes ~30 seconds</p>
        {onCancel && (
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" className={BTN_GHOST} onClick={onCancel}>
              <span className="text-cap-trim block">Cancel</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

interface ThumbProps {
  url: string;
  label: string;
  onClick: () => void;
}

function Thumb({ url, label, onClick }: ThumbProps): JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <ImageWithLoader
        frameClassName="oq-image-frame--sized w-full h-32"
        src={url}
        alt={`${label} screenshot`}
        className="w-full h-full object-contain"
      />
      <span className="absolute top-1.5 left-1.5 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-semibold tracking-wider">
        {label}
      </span>
    </div>
  );
}

interface LightboxProps {
  beforeUrl: string;
  afterUrl: string;
  initial: 'before' | 'after';
  onClose: () => void;
}

function Lightbox({ beforeUrl, afterUrl, initial, onClose }: LightboxProps): JSX.Element {
  const [showing, setShowing] = useState<'before' | 'after'>(initial);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Escape, focus trapping and focus restore. The a/b flip keys below stay
  // local to this lightbox.
  useModalA11y(true, dialogRef, onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'a' || e.key === 'A') setShowing('after');
      else if (e.key === 'b' || e.key === 'B') setShowing('before');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overlay = (
    <div
      ref={dialogRef}
      aria-label="Before and after screenshots"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-8 py-16"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ModalTitle icon="image" tone="on-dark" className="fixed top-4 left-4 max-w-[calc(100%_-_5rem)] rounded-md bg-black/60 px-3 py-2 text-sm text-white">Before and after</ModalTitle>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="fixed top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <ImageWithLoader
        frameClassName="oq-image-frame--sized"
        frameStyle={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}
        src={showing === 'before' ? beforeUrl : afterUrl}
        alt={`${showing.toUpperCase()} screenshot`}
        className="max-w-full max-h-full object-contain rounded-lg"
      />
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-3 py-2 bg-black/60 text-white text-xs rounded-full">
        Press [a] / [b] to flip. Showing {showing.toUpperCase()}
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

/** Return only the first two sentences of the AI explanation. */
function capFirstTwoSentences(text: string): string {
  const parts = text.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0);
  return parts.slice(0, 2).join(' ');
}

/** True when any meta-line field is present, to avoid rendering an empty row. */
function hasMeta(v: IssueVerification): boolean {
  return Boolean(v.axeResult) || v.confidence != null || v.viewportMismatch;
}

function renderActions(status: VerificationStatus, p: VerificationCardProps): ReactNode {
  // While a status action (mark verified / reopen) is mid-flight, every
  // action button disables and the active one swaps its label.
  const pending = p.statusActionPending ?? null;
  const anyPending = pending !== null;
  const markLabel = (idle: string): string => (pending === 'verify' ? 'Marking...' : idle);
  const reopenLabel = (idle: string): string => (pending === 'reopen' ? 'Reopening...' : idle);
  switch (status) {
    case 'passed':
      return (
        <>
          <button type="button" className={BTN_PRIMARY} onClick={p.onMarkVerified} disabled={anyPending}>
            <span className="text-cap-trim block">{markLabel('Mark as verified')}</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onReopen} disabled={anyPending}>
            <span className="text-cap-trim block">{reopenLabel('Reopen')}</span>
          </button>
        </>
      );
    case 'passed_with_caveat':
      return (
        <>
          <button type="button" className={BTN_PRIMARY} onClick={p.onMarkVerified} disabled={anyPending}>
            <span className="text-cap-trim block">{markLabel('Mark verified')}</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onReopen} disabled={anyPending}>
            <span className="text-cap-trim block">{reopenLabel('Reopen')}</span>
          </button>
        </>
      );
    case 'failed':
      return (
        <>
          <button type="button" className={BTN_PRIMARY} onClick={p.onReopen} disabled={anyPending}>
            <span className="text-cap-trim block">{reopenLabel('Reopen as regressed')}</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onRetry} disabled={anyPending}>
            <span className="text-cap-trim block">Retry</span>
          </button>
        </>
      );
    case 'inconclusive':
      // F-state has no default emphasis (spec §7.1). "Mark as verified" means
      // "I've reviewed the AI's output, flip status to verified" (same
      // handler as State C's "Mark as verified" primary action).
      return (
        <>
          <button type="button" className={BTN_GHOST} onClick={p.onMarkVerified} disabled={anyPending}>
            <span className="text-cap-trim block">{markLabel('Mark as verified')}</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onReopen} disabled={anyPending}>
            <span className="text-cap-trim block">{reopenLabel('Reopen')}</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onRetry} disabled={anyPending}>
            <span className="text-cap-trim block">Retry</span>
          </button>
        </>
      );
    case 'error':
      return (
        <>
          <button type="button" className={BTN_PRIMARY} onClick={p.onRetry} disabled={anyPending}>
            <span className="text-cap-trim block">Try again</span>
          </button>
          <button type="button" className={BTN_GHOST} onClick={p.onSkip} disabled={anyPending}>
            <span className="text-cap-trim block">Skip verification</span>
          </button>
        </>
      );
    case 'pending':
      // Pending status without inProgress is a transient race; render nothing.
      return null;
    default:
      return null;
  }
}

function Spinner(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeDasharray="40 60"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

interface StatusIconProps {
  status: VerificationStatus;
}

function StatusIcon({ status }: StatusIconProps): JSX.Element | null {
  // SVG icons only — no emoji (CLAUDE.md UI rule).
  const SIZE = 12;
  const common = {
    width: SIZE,
    height: SIZE,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 3,
    'aria-hidden': true,
  };
  switch (status) {
    case 'passed':
      return (
        <svg {...common}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'passed_with_caveat':
      return (
        <svg {...common}>
          <path d="M12 9v4M12 17h.01" />
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      );
    case 'failed':
    case 'error':
      return (
        <svg {...common}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'inconclusive':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
        </svg>
      );
    case 'pending':
      return <Spinner />;
    default:
      return null;
  }
}
